import {
  GLOBAL_EVENTS,
  PUBLIC_MESSAGE_EVENTS,
  ROLE_GROUPS,
  SOCKET_ROOMS
} from '@moal/api'
import { useContext, useEffect } from 'react'
import {
  ADD_MESSAGE,
  CLEAR_PUBLIC_MESSAGES,
  DELETE_MESSAGE,
  EDIT_MESSAGE,
  INITIAL_LOAD,
  LOAD_MORE,
  MARK_AS_QUESTION,
  UNMARK_ALL_AS_QUESTIONS,
  UNMARK_AS_QUESTION
} from '../constants/actions/messageListActions'
import { MEMBERS } from '../constants/chatRooms'
import { SocketContext } from '../context/socketContext'
import {
  getModPublicMessages as getModPublicMessagesAPI,
  getPublicMessages as getPublicMessagesAPI,
  getPublicMessagesSearch as getPublicMessagesSearchAPI
} from '../utils/APIs/publicMessages'

export const usePublicMessages = ({
  dispatch,
  channel,
  scrollRef,
  isMobileBrowser,
  setKeyModifier,
  modView = false,
  showShadowBanned = false,
  user
}) => {
  const { socket, joinRoom } = useContext(SocketContext)

  useEffect(() => {
    if (channel === MEMBERS && socket) {
      publicSockets()
    } else {
      publicSockets(false)
    }
    return () => {
      publicSockets(false)
    }
  }, [socket, channel])

  const publicSockets = (on = true) => {
    if (on) {
      if (user && ROLE_GROUPS.STAFF.includes(user.role.role)) {
        joinRoom(socket, SOCKET_ROOMS.LIVESTREAM.STAFF_PUBLIC_CHAT)
      } else {
        joinRoom(socket, SOCKET_ROOMS.LIVESTREAM.PUBLIC_CHAT)
      }

      socket.on(GLOBAL_EVENTS.CONNECT, () => {
        if (user && ROLE_GROUPS.STAFF.includes(user.role.role)) {
          joinRoom(socket, SOCKET_ROOMS.LIVESTREAM.STAFF_PUBLIC_CHAT)
        } else {
          joinRoom(socket, SOCKET_ROOMS.LIVESTREAM.PUBLIC_CHAT)
        }
      })
      socket.on(PUBLIC_MESSAGE_EVENTS.POST, handlePostPublicMessage)
      socket.on(PUBLIC_MESSAGE_EVENTS.PUT, handleEditPublicMessage)
      socket.on(PUBLIC_MESSAGE_EVENTS.DELETE, handleDeletePublicMessage)
      if (!modView) {
        socket.on(PUBLIC_MESSAGE_EVENTS.CLEAR, handleClearPublicMessages)
      } else {
        socket.on(PUBLIC_MESSAGE_EVENTS.MARK_AS_QUESTION, handleMarkAsQuestion)
        socket.on(
          PUBLIC_MESSAGE_EVENTS.UNMARK_AS_QUESTION,
          handleUnMarkAsQuestion
        )
        socket.on(
          PUBLIC_MESSAGE_EVENTS.UNMARK_ALL_AS_QUESTIONS,
          handleUnmarkAllAsQuestions
        )
      }
    } else {
      if (socket) {
        socket.emit(
          GLOBAL_EVENTS.LEAVE_ROOM,
          SOCKET_ROOMS.LIVESTREAM.PUBLIC_CHAT
        )
        socket.emit(
          GLOBAL_EVENTS.LEAVE_ROOM,
          SOCKET_ROOMS.LIVESTREAM.STAFF_PUBLIC_CHAT
        )
        socket.off(PUBLIC_MESSAGE_EVENTS.POST, handlePostPublicMessage)
        socket.off(PUBLIC_MESSAGE_EVENTS.PUT, handleEditPublicMessage)
        socket.off(PUBLIC_MESSAGE_EVENTS.DELETE, handleDeletePublicMessage)
        socket.off(PUBLIC_MESSAGE_EVENTS.MARK_AS_QUESTION, handleMarkAsQuestion)
        socket.off(
          PUBLIC_MESSAGE_EVENTS.UNMARK_AS_QUESTION,
          handleUnMarkAsQuestion
        )
        socket.off(
          PUBLIC_MESSAGE_EVENTS.UNMARK_ALL_AS_QUESTIONS,
          handleUnmarkAllAsQuestions
        )
      }
    }
  }

  const handlePostPublicMessage = (data) => {
    if (shadowBanCheck(data)) {
      dispatch({ type: ADD_MESSAGE, payload: data })
      if (scrollRef.current === 0 && isMobileBrowser) {
        setKeyModifier((old) => old + 1)
      }
    }
  }

  const handleEditPublicMessage = (data) => {
    if (shadowBanCheck(data)) {
      dispatch({ type: EDIT_MESSAGE, payload: data })
    }
  }

  const handleDeletePublicMessage = (data) => {
    dispatch({ type: DELETE_MESSAGE, payload: data })
  }

  const handleClearPublicMessages = () => {
    dispatch({ type: CLEAR_PUBLIC_MESSAGES, payload: {} })
  }

  const shadowBanCheck = (message) => {
    return (
      !message.hidden ||
      (user && message.user._id === user._id) ||
      (modView && showShadowBanned)
    )
  }

  const getPublicMessages = async ({ initialLoad, messages, search }) => {
    let loadMore = ''
    if (!initialLoad && messages?.length > 0) {
      loadMore = messages[messages.length - 1].postdate
    }

    let response
    if (search) {
      response = await getPublicMessagesSearchAPI({
        search,
        loadMore
      })
    } else {
      if (modView) {
        response = await getModPublicMessagesAPI({
          loadMore
        })
      } else {
        response = await getPublicMessagesAPI({
          loadMore
        })
      }
    }

    if (response && response.status === 200) {
      handleGetPublicMessage(initialLoad, response.data)
    }
  }

  const handleGetPublicMessage = (initialLoad, data) => {
    const scrubbed = data.filter((each) => shadowBanCheck(each))

    if (initialLoad) {
      dispatch({ type: INITIAL_LOAD, payload: scrubbed })
    } else {
      dispatch({ type: LOAD_MORE, payload: scrubbed })
    }
  }

  const handleMarkAsQuestion = (data) => {
    dispatch({ type: MARK_AS_QUESTION, payload: data })
  }

  const handleUnMarkAsQuestion = (data) => {
    dispatch({ type: UNMARK_AS_QUESTION, payload: data })
  }

  const handleUnmarkAllAsQuestions = () => {
    dispatch({ type: UNMARK_ALL_AS_QUESTIONS, payload: {} })
  }

  return { getPublicMessages }
}
